import { useRef } from "react";
import { Loader2 } from "lucide-react";
import { fileMatchesAccept } from "../../lib/journey";
import { toast } from "sonner";
import { Icon } from "../msd/Icon";
import { cx } from "../msd/ui";

const sizeLabel = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`);

// .up, and .gico inside it — the row you tap to choose a file
export const UP_ROW =
  "flex w-full cursor-pointer items-center gap-3 rounded-[14px] border-0 bg-msd-surface px-[14px] py-3 text-left text-msd-ink shadow-msd-e1 hover:shadow-msd-e2";
export const upIconCls = (done) =>
  cx(
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px]",
    done ? "bg-[#17B26A] text-white" : "bg-[rgba(120,120,128,.16)] text-msd-muted",
  );

const ICON_BTN = "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-msd-muted hover:bg-msd-surface";

// A row to tap plus the list of files in it. Files upload as they are picked
// (see useBookingUploads), and each row shows where its upload is.
export default function UploadSlot({ label, hint, kinds, accept, multiple = false, required = false, items, onPick, onRemove, onRetry, testid }) {
  const inputRef = useRef(null);

  const choose = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (picked.some((f) => !fileMatchesAccept(f, accept))) {
      toast.error(`${label} accepts ${kinds} only.`);
      return;
    }
    onPick(picked);
  };

  const stored = items.length > 0 && items.every((item) => item.status === "done");

  return (
    <div data-testid={testid}>
      <button type="button" onClick={() => inputRef.current?.click()} className={UP_ROW}>
        <span className={upIconCls(stored)}>
          <Icon name={stored ? "check" : multiple ? "img" : "doc"} size={18} stroke={2.2} />
        </span>
        <span className="min-w-0 grow">
          <b className="block text-[15px] font-semibold">
            {label}
            {required ? <span className="text-[#F04438]"> *</span> : null}
          </b>
          <span className="block truncate text-[13px] text-msd-muted">
            {items.length && !multiple ? "Replace file" : multiple ? "Choose files" : "Choose a file"} · {hint || `${kinds} only`}
          </span>
        </span>
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" data-testid={`${testid}-input`} onChange={choose} />

      {items.length > 0 && (
        <ul className="m-0 mt-2 flex list-none flex-col gap-[6px] p-0">
          {items.map((item) => (
            <li
              key={item.id}
              className={cx(
                "flex items-center gap-3 rounded-xl px-[14px] py-[9px] text-[14px]",
                item.status === "error" ? "bg-msd-brick-soft" : "bg-msd-ground",
              )}
              data-status={item.status}
            >
              <Icon name="doc" size={16} stroke={2} className="shrink-0 text-msd-muted" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-msd-ink">{item.name}</span>
                <span className={cx("block text-[12.5px]", item.status === "error" ? "text-msd-brick-ink" : "text-msd-muted")}>
                  {item.status === "uploading" && "Uploading…"}
                  {item.status === "done" && `Uploaded · ${sizeLabel(item.size)}`}
                  {item.status === "error" && (item.error || "Upload failed")}
                </span>
              </span>
              {item.status === "uploading" && <Loader2 size={16} className="shrink-0 animate-spin text-[#3A5F94]" />}
              {item.status === "done" && <Icon name="circlecheck" size={18} stroke={2} color="#17B26A" className="shrink-0" />}
              {item.status === "error" && (
                <>
                  <Icon name="warn" size={16} stroke={2} color="#F04438" className="shrink-0" />
                  <button type="button" onClick={() => onRetry(item.id)} aria-label={`Retry ${item.name}`} className={ICON_BTN}>
                    <Icon name="reset" size={15} stroke={2} />
                  </button>
                </>
              )}
              <button type="button" onClick={() => onRemove(item.id)} aria-label={`Remove ${item.name}`} className={ICON_BTN}>
                <Icon name="x" size={15} stroke={2} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
